import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { aboutInfo } from '@/mocks/articles';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9u1ob5ca23r0tr1aoug';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, value as string);
        }
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Page Header */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              关于尤物视频
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-[700px]">
              {aboutInfo.brandIntro}
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <div className="max-w-[700px] mx-auto">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-star-line text-2xl text-primary-600"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">我们的使命</h2>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              品牌历程
            </h2>
            <div className="max-w-[700px] mx-auto">
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-primary-200"></div>
                <div className="space-y-8">
                  {aboutInfo.milestones.map((milestone) => (
                    <div key={milestone.year} className="flex gap-5 items-start relative">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0 relative z-10">
                        {milestone.year.slice(2)}
                      </div>
                      <div className="flex-1 pt-2">
                        <h3 className="text-sm font-medium text-foreground-900 mb-1">
                          <strong>{milestone.year}年</strong>
                        </h3>
                        <p className="text-sm text-foreground-600">{milestone.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              核心团队
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-[1000px] mx-auto">
              {aboutInfo.team.map((member) => (
                <div key={member.name} className="text-center p-5 rounded-xl bg-background-50">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[1000px] mx-auto">
              {aboutInfo.testimonials.map((testimonial) => (
                <div key={testimonial.name} className="p-6 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{testimonial.name}</p>
                      <p className="text-xs text-foreground-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-[1000px] mx-auto">
              {/* Contact Info */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">联系电话</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-4">
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="微信">
                    <i className="ri-wechat-line text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="微博">
                    <i className="ri-weibo-line text-lg"></i>
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-xl p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-5">
                  给我们留言
                </h3>
                <form onSubmit={handleSubmit} data-readdy-form="">
                  {/* Honeypot */}
                  <div className="sp-fld-wrap">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm text-center">
                      感谢您的留言，我们会尽快回复！
                    </div>
                  )}
                  {formStatus === 'error' && formError && (
                    <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm text-center">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '尤物视频是什么？', a: '尤物视频是一个精选高颜值视频内容平台，涵盖时尚、美妆、生活方式等多元内容，为您提供最优质的视觉享受。' },
                { q: '内容多久更新一次？', a: '我们每天都会更新新鲜内容，确保您每天都有新的精彩视频可以发现。' },
                { q: '如何成为尤物视频的创作者？', a: '您可以通过关于我们页面的联系方式与我们取得联系，提交您的作品集，我们的编辑团队会进行审核。' },
                { q: '尤物视频是否免费？', a: '是的，尤物视频平台上的所有内容均可免费浏览，无需付费订阅。' },
                { q: '如何联系我们？', a: '您可以通过页面上的联系表单、发送邮件至 hello@youwuvideo.com 或拨打 400-888-9999 与我们取得联系。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    {faq.q}
                    <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于尤物视频',
            description: aboutInfo.brandIntro,
            url: 'https://glamzone3.github.io/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '尤物视频',
              url: 'https://glamzone3.github.io',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://glamzone3.github.io',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '关于我们',
                  item: 'https://glamzone3.github.io/about',
                },
              ],
            },
            mainEntity: {
              '@type': 'Organization',
              name: '尤物视频',
              url: 'https://glamzone3.github.io',
              email: aboutInfo.contact.email,
              telephone: aboutInfo.contact.phone,
              address: {
                '@type': 'PostalAddress',
                streetAddress: aboutInfo.contact.address,
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '尤物视频是什么？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '尤物视频是一个精选高颜值视频内容平台，涵盖时尚、美妆、生活方式等多元内容，为您提供最优质的视觉享受。',
                },
              },
              {
                '@type': 'Question',
                name: '内容多久更新一次？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '我们每天都会更新新鲜内容，确保您每天都有新的精彩视频可以发现。',
                },
              },
              {
                '@type': 'Question',
                name: '如何成为尤物视频的创作者？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过关于我们页面的联系方式与我们取得联系，提交您的作品集，我们的编辑团队会进行审核。',
                },
              },
              {
                '@type': 'Question',
                name: '尤物视频是否免费？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，尤物视频平台上的所有内容均可免费浏览，无需付费订阅。',
                },
              },
              {
                '@type': 'Question',
                name: '如何联系我们？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过页面上的联系表单、发送邮件至 hello@youwuvideo.com 或拨打 400-888-9999 与我们取得联系。',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}